import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { DashboardFilterService } from '../../features/dashboard/services/dashboard-filter.service';
import { ReconStatementFilterService } from '../../features/recon-statement/services/recon-statement-filter.service';
import { VendorUploadFilterService } from '../../features/vendor-upload/services/vendor-upload-filter.service';
import { SecureStorageService } from './secure-storage.service';

export interface UserInfo {
  userId: number;
  roleId: number;
  name: string;
  initials: string;
  email: string;
  role: string;
  department: string;
}

export interface AccessManagement {
  ROLE_ID: number;
  ROLE_NAME: string;
  Admin_Rule_Action_Access?: boolean;
  Admin_Rule_View_Access?: boolean;
  Vendor_Sugg_REMOVE_MATCH: boolean;
  Partially_Mat_REMOVE_MATCH: boolean;
  ML_Remove_Match: boolean;
  Open_Tran_Approve: boolean;
  Vendor_Sugg_Approve: boolean;
  Partially_Mat_Approve: boolean;
  Open_Tran_Reject: boolean;
  Partially_Mat_Reject: boolean;
  Manual_SUBMIT: boolean;
  ML_Approve: boolean;
  FILE_UPLOAD: boolean;
  CONTRA_MATCH: boolean;
  FORCE_MATCH: boolean;
  Open_Tran_Tab: boolean;
  Vendor_suggested_Tab: boolean;
  Subsequenthly_Acc_Tab: boolean;
  Partially_Mat_Tab: boolean;
  ML_Suggested_Tab: boolean;
  Vendor_Upload_Truncate: boolean;
  Vendor_Upload_Approve_Reject: boolean;
  Vendor_Upload_Upload_Ledger: boolean;
  Recon_Statement_Submit: boolean;
  Recon_Statement_Sign_Off: boolean;
  DNCN_Sub_To_L2_Tab: boolean;
  DNCN_Open_Map: boolean;
  DNCN_Open_Dispute: boolean;
  DNCN_Disputed_Resolve: boolean;
  DNCN_Bulk_Create_Request: boolean;
  DNCN_Bulk_Upload_Approve: boolean;
  DNCN_Bulk_Upload_Reject: boolean;
  ML_Sugg_Rules_Approve: boolean;
  DNCN_Open_ClubDNR: boolean;
  Admin_DQ_Dashboard: boolean;
}

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    userDetails: {
      userId: number;
      roleId: number;
      userName: string;
      roleName: string;
      email: string;
      password: string | null;
    };
    token: string;
    accessManagement: AccessManagement[];
    responseData: {
      header: string;
      message: string;
      type: string;
    };
  };
  totalRecords: number | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly STORAGE_KEY = 'fk_recon_user';
  private readonly ACCESS_KEY = 'fk_recon_access';
  private readonly TOKEN_KEY = 'fk_recon_token';
  private http = inject(HttpClient);
  private router = inject(Router);
  private secureStorage = inject(SecureStorageService);
  private dashboardFilterService = inject(DashboardFilterService);
  private reconStatementFilterService = inject(ReconStatementFilterService);
  private vendorUploadFilterService = inject(VendorUploadFilterService);

  user = signal<UserInfo | null>(this.loadUser());
  isAuthenticated = signal(!!this.loadUser());
  accessManagement = signal<AccessManagement | null>(this.loadAccess());

  async login(email: string): Promise<UserInfo> {
    const res = await firstValueFrom(
      this.http.post<LoginResponse>(
        API_ENDPOINTS.AUTH.LOGIN,
        { email }
      )
    );

    if (!res.success) {
      throw new Error(res.message || 'Login failed');
    }

    const details = res.data.userDetails;
    const name = details.userName || email.split('@')[0];

    const user: UserInfo = {
      userId: details.userId,
      roleId: details.roleId,
      name,
      initials: name
        .split(' ')
        .map((w: string) => w[0])
        .join('')
        .toUpperCase()
        .slice(0, 2),
      email: details.email,
      role: details.roleName,
      department: '',
    };

    const access = res.data.accessManagement?.[0] ?? null;
    const token = res.data.token;

    this.storeUser(user);
    this.storeAccess(access);
    this.storeToken(token);
    this.user.set(user);
    this.accessManagement.set(access);
    this.isAuthenticated.set(true);
    return user;
  }

  logout(): void {
    localStorage.clear();
    sessionStorage.clear();
    this.dashboardFilterService.reset();
    this.reconStatementFilterService.reset();
    this.vendorUploadFilterService.reset();
    this.user.set(null);
    this.accessManagement.set(null);
    this.isAuthenticated.set(false);
    this.router.navigate(['/signin']);
  }

  private storeUser(user: UserInfo): void {
    this.secureStorage.setSessionObject(this.STORAGE_KEY, user);
  }

  private storeAccess(access: AccessManagement | null): void {
    this.secureStorage.setSessionObject(this.ACCESS_KEY, access);
  }

  private storeToken(token: string): void {
    sessionStorage.setItem(this.TOKEN_KEY, token);
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.TOKEN_KEY);
  }

  hasAdminRuleActionAccess(): boolean {
    const access = this.accessManagement() ?? this.loadAccess();
    return !!access?.Admin_Rule_Action_Access;
  }

  hasAdminRuleViewAccess(): boolean {
    const access = this.accessManagement() ?? this.loadAccess();
    return !!access?.Admin_Rule_View_Access;
  }

  private loadUser(): UserInfo | null {
    return this.secureStorage.getSessionObject<UserInfo>(this.STORAGE_KEY);
  }

  private loadAccess(): AccessManagement | null {
    return this.secureStorage.getSessionObject<AccessManagement>(this.ACCESS_KEY);
  }
}
